import { useEffect, useRef } from "react";
import { colorForDensity } from "./heatmap-color-ramp";

type HeatmapPoint = { x: number; y: number };

type WallPreviewHeatmapProps = {
  points: HeatmapPoint[];
  width: number;
  height: number;
};

const POINT_RADIUS = 70;
const POINT_DENSITY_ALPHA = 0.22;
const MIN_VISIBLE_ALPHA = 90;

export function WallPreviewHeatmap({
  points,
  width,
  height,
}: WallPreviewHeatmapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(
    function effectOnPointsChanged() {
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");

      if (!canvas || !context || width <= 0 || height <= 0) {
        return;
      }

      // Pass 1: accumulate raw density (grayscale alpha only) off-screen — overlapping
      // points naturally stack into a higher alpha via normal "source-over" compositing.
      const densityCanvas = document.createElement("canvas");
      densityCanvas.width = width;
      densityCanvas.height = height;
      const densityContext = densityCanvas.getContext("2d");

      if (!densityContext) {
        return;
      }

      for (const point of points) {
        const gradient = densityContext.createRadialGradient(
          point.x,
          point.y,
          0,
          point.x,
          point.y,
          POINT_RADIUS,
        );

        gradient.addColorStop(0, `rgba(0, 0, 0, ${POINT_DENSITY_ALPHA})`);
        gradient.addColorStop(1, "rgba(0, 0, 0, 0)");

        densityContext.fillStyle = gradient;
        densityContext.fillRect(
          point.x - POINT_RADIUS,
          point.y - POINT_RADIUS,
          POINT_RADIUS * 2,
          POINT_RADIUS * 2,
        );
      }

      // Pass 2: remap accumulated density to the sequential color ramp.
      const imageData = densityContext.getImageData(0, 0, width, height);
      const { data } = imageData;

      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3];

        if (alpha === 0) {
          continue;
        }

        const [r, g, b] = colorForDensity(alpha / 255);

        data[i] = r;
        data[i + 1] = g;
        data[i + 2] = b;
        data[i + 3] = Math.min(255, alpha + MIN_VISIBLE_ALPHA);
      }

      context.clearRect(0, 0, width, height);
      context.putImageData(imageData, 0, 0);
    },
    [points, width, height],
  );

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="absolute inset-0 pointer-events-none"
    />
  );
}
